import {
  BasketItem,
  Breakpoint,
  ButtonSize,
  ButtonTheme,
  CategoryBasket,
  FontWeight,
  FormButtonTheme,
  MatchToCategoryPopup,
  MatchToCategoryPopupItem,
  Typography,
  TypographyVariant,
  UseState,
} from '@elearning-platform/ui';
import React, { useMemo, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { CategoryBasketData, CategoryBasketItem, CategoryBasketItemWithCategoryData } from '../../interfaces';

import {
  Categories,
  Container,
  ResultContentContainer,
  ResultImage,
  ResultImageContainer,
  ResultWrapper,
  StyledButton,
  StyledFormButton,
  TryAgainContainer,
} from './matchToCategory.styled';
import { MatchToCategoryProps, PopupData } from './matchToCategory.types';

export const MatchToCategory: React.FC<MatchToCategoryProps> = (props: MatchToCategoryProps): JSX.Element => {
  const { callComplete, content, baseUrl, failImage }: MatchToCategoryProps = props;
  const { t }: TransProps<never> = useTranslation();
  const [userSelection, setUserSelection]: UseState<Record<string, string[]>> = useState({});
  const [popupData, setPopupData]: UseState<PopupData | undefined> = useState();
  const [isVerified, setIsVerified]: UseState<boolean> = useState(false);
  const [attemptsCount, setAttemptsCount]: UseState<number> = useState(0);
  const isSmallDesktop: boolean = useMediaQuery({
    query: Breakpoint.SmallDesktop,
  });
  const allItems: CategoryBasketItemWithCategoryData[] = useMemo((): CategoryBasketItemWithCategoryData[] => (
    content.categories.flatMap((category: CategoryBasketData): CategoryBasketItemWithCategoryData[] => (
      category.items.map((item: CategoryBasketItem): CategoryBasketItemWithCategoryData => (
        { ...item, categoryId: category.id, categoryLabel: category.label }
      ))
    ))
  ), [content]);

  const isAllAnswersCorrect: boolean = useMemo((): boolean => {
    for (const category of content.categories) {
      const selectedItems: string[] | undefined = userSelection[category.id];

      if (
        selectedItems?.length !== category.items.length
        || selectedItems?.find((item: string): boolean => !category.items.find((i: CategoryBasketItem): boolean => i.id === item))
      ) {
        return false;
      }
    }

    return true;
  }, [userSelection, content]);

  const popupFields: MatchToCategoryPopupItem[] = useMemo(
    (): MatchToCategoryPopupItem[] => !popupData ? [] : allItems
      .map((item: CategoryBasketItemWithCategoryData): MatchToCategoryPopupItem => ({
        id: item.id,
        label: item.label,
        isSelected: !!userSelection[popupData.categoryId]?.includes(item.id),
        initialSelectedCategory: content.categories
          .filter((cat: CategoryBasketData): boolean => cat.id !== popupData.categoryId)
          .find((cat: CategoryBasketData): boolean => userSelection[cat.id]?.includes(item.id))?.label,
      })),
    [content, userSelection, popupData, allItems]
  );

  const handleSelectionChange: (selectedItems: string[]) => void = (selectedItems: string[]): void => {
    if (popupData) {
      const newSelection: Record<string, string[]> = {};

      for (const categoryId in userSelection) {
        if (userSelection[categoryId]) {
          newSelection[categoryId] = userSelection[categoryId].filter(
            (item: string): boolean => !selectedItems.includes(item)
          );
        }
      }

      setUserSelection({
        ...newSelection,
        [popupData.categoryId]: selectedItems,
      });
      setPopupData(undefined);
    }
  };

  const resetAttempt: () => void = (): void => {
    setAttemptsCount(attemptsCount + 1);
    setIsVerified(false);
    setUserSelection({});
  };

  const handleVerify: () => void = (): void => {
    setIsVerified(true);
    if (isAllAnswersCorrect || attemptsCount >= 1) {
      callComplete();
    }
  };

  return (
    <Container>
      {isVerified && !isAllAnswersCorrect && attemptsCount < 1 ? (
        <ResultWrapper>
          <ResultContentContainer>
            <Typography fontWeight={FontWeight.Bold} variant={isSmallDesktop ? TypographyVariant.DisplaySM : TypographyVariant.TextXL}>
              {t('competence.app.scrollLesson.componentsV2.matchToCategory.failHeadline')}
            </Typography>
            <TryAgainContainer>
              <Typography variant={isSmallDesktop ? TypographyVariant.DisplayXS : TypographyVariant.TextXL}>
                {t('competence.app.scrollLesson.componentsV2.matchToCategory.failSupportingText')}
              </Typography>
              {!isSmallDesktop && (
                <ResultImageContainer>
                  <ResultImage src={`${baseUrl}${failImage}`} />
                </ResultImageContainer>
              )}
              <StyledButton
                fullWidth={!isSmallDesktop}
                label={t('competence.app.scrollLesson.componentsV2.matchToCategory.tryAgain')}
                onClick={resetAttempt}
                size={ButtonSize.Large}
                variant={ButtonTheme.PrimaryBrand}
              />
            </TryAgainContainer>
          </ResultContentContainer>
          {!!isSmallDesktop && (
            <ResultImageContainer>
              <ResultImage src={`${baseUrl}${failImage}`} />
            </ResultImageContainer>
          )}
        </ResultWrapper>
      ) : (
        <>
          <Typography fontWeight={FontWeight.SemiBold} variant={TypographyVariant.TextLG}>
            {isVerified
              ? isAllAnswersCorrect
                ? content.successText
                : content.failureText
              : content.taskTitle
            }
          </Typography>
          <Categories $isSingleChoice={!!content.isSingleChoice}>
            {
              content.categories.map((category: CategoryBasketData): JSX.Element => (
                <CategoryBasket
                  categoryData={{
                    label: category.label,
                    id: category.id,
                  }}
                  isVerified={isVerified}
                  isVertical={content.isSingleChoice}
                  items={allItems.map((item: CategoryBasketItemWithCategoryData): BasketItem => (
                    {
                      label: item.label,
                      description: item.description,
                      id: item.id,
                      isSelected: userSelection[category.id]?.includes(item.id),
                      category: {
                        id: item.categoryId,
                        label: item.categoryLabel,
                      },
                    }
                  ))}
                  itemsName={content.itemsName}
                  key={category.id}
                  onItemsSelectionOpen={(): void => setPopupData({ categoryId: category.id, categoryLabel: category.label })}
                />
              ))
            }
          </Categories>
          {!isVerified && Object.keys(userSelection).length > 0 && (
            <StyledFormButton
              label={t('competence.app.scrollLesson.componentsV2.matchToCategory.accept')}
              onClick={handleVerify}
              variant={FormButtonTheme.SecondaryBrand}
            />
          )}
        </>
      )}
      <MatchToCategoryPopup
        categoryName={popupData?.categoryLabel ?? ''}
        disclaimer={content.popupDisclaimer}
        elementsName={content.itemsName}
        fields={popupFields.sort((a: MatchToCategoryPopupItem, b: MatchToCategoryPopupItem): number => a.id.localeCompare(b.id))}
        isSingleChoice={content.isSingleChoice}
        isVisible={!!popupData}
        onChange={handleSelectionChange}
        onClose={(): void => setPopupData(undefined)}
      />
    </Container>
  );
};
